import type { CanvasElement } from '@extapia/shared';
import { indexBetween, newId, newNonce, newSeed, now } from '@extapia/shared';

// Minimal starter templates. Each returns an array of ready-to-insert elements
// positioned around origin (0,0). Consumer must offset if placing elsewhere.

const base = (over: Partial<CanvasElement>) =>
  ({
    id: newId(),
    angle: 0,
    strokeColor: '#111827',
    fillColor: 'transparent',
    fillStyle: 'hachure',
    strokeWidth: 2,
    strokeStyle: 'solid',
    roughness: 1,
    opacity: 100,
    seed: newSeed(),
    version: 1,
    versionNonce: newNonce(),
    isDeleted: false,
    groupIds: [],
    frameId: null,
    boundElements: [],
    link: null,
    locked: false,
    updated: now(),
    index: 'V',
    ...over,
  }) as CanvasElement;

const chainIndex = (list: CanvasElement[]) => {
  let prev = 'V';
  for (const el of list) {
    el.index = indexBetween(prev, null);
    prev = el.index;
  }
  return list;
};

export const TEMPLATES: Array<{ id: string; label: string; description: string; build: () => CanvasElement[] }> = [
  {
    id: 'blank',
    label: 'Blank',
    description: 'Kanvas kosong.',
    build: () => [],
  },
  {
    id: 'flowchart',
    label: 'Flowchart',
    description: 'Start → Proses → Decision → End dengan panah.',
    build: () =>
      chainIndex([
        base({ type: 'ellipse', x: 100, y: 100, width: 140, height: 60, strokeColor: '#0284c7' }),
        base({ type: 'text', x: 145, y: 118, width: 60, height: 24, text: 'Start', fontSize: 20, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Start', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'rect', x: 320, y: 100, width: 160, height: 60, roundness: null, strokeColor: '#0284c7' } as Partial<CanvasElement>),
        base({ type: 'text', x: 355, y: 118, width: 90, height: 24, text: 'Process', fontSize: 20, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Process', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'diamond', x: 540, y: 90, width: 160, height: 80, roundness: null, strokeColor: '#0284c7' } as Partial<CanvasElement>),
        base({ type: 'text', x: 580, y: 118, width: 80, height: 24, text: 'Decision?', fontSize: 18, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Decision?', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'ellipse', x: 770, y: 100, width: 120, height: 60, strokeColor: '#0284c7' } as Partial<CanvasElement>),
        base({ type: 'text', x: 810, y: 118, width: 40, height: 24, text: 'End', fontSize: 20, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'End', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'arrow', x: 240, y: 130, width: 80, height: 0, points: [[0, 0], [80, 0]], startBinding: null, endBinding: null, startArrowhead: null, endArrowhead: 'arrow', elbowed: false } as Partial<CanvasElement>),
        base({ type: 'arrow', x: 480, y: 130, width: 60, height: 0, points: [[0, 0], [60, 0]], startBinding: null, endBinding: null, startArrowhead: null, endArrowhead: 'arrow', elbowed: false } as Partial<CanvasElement>),
        base({ type: 'arrow', x: 700, y: 130, width: 70, height: 0, points: [[0, 0], [70, 0]], startBinding: null, endBinding: null, startArrowhead: null, endArrowhead: 'arrow', elbowed: false } as Partial<CanvasElement>),
      ]),
  },
  {
    id: 'mindmap',
    label: 'Mindmap',
    description: 'Root topic + 4 cabang.',
    build: () =>
      chainIndex([
        base({ type: 'ellipse', x: 400, y: 240, width: 200, height: 80, fillColor: '#fde68a', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'text', x: 460, y: 268, width: 80, height: 24, text: 'Idea', fontSize: 24, fontFamily: 3, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Idea', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'rect', x: 100, y: 120, width: 160, height: 50, roundness: { type: 'round', value: 8 }, fillColor: '#bfdbfe', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'rect', x: 100, y: 400, width: 160, height: 50, roundness: { type: 'round', value: 8 }, fillColor: '#bbf7d0', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'rect', x: 740, y: 120, width: 160, height: 50, roundness: { type: 'round', value: 8 }, fillColor: '#fbcfe8', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'rect', x: 740, y: 400, width: 160, height: 50, roundness: { type: 'round', value: 8 }, fillColor: '#c4b5fd', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'line', x: 260, y: 145, width: 140, height: 110, points: [[0, 0], [140, 110]] } as Partial<CanvasElement>),
        base({ type: 'line', x: 260, y: 415, width: 140, height: -105, points: [[0, 0], [140, -105]] } as Partial<CanvasElement>),
        base({ type: 'line', x: 600, y: 255, width: 140, height: -110, points: [[0, 0], [140, -110]] } as Partial<CanvasElement>),
        base({ type: 'line', x: 600, y: 305, width: 140, height: 110, points: [[0, 0], [140, 110]] } as Partial<CanvasElement>),
      ]),
  },
  {
    id: 'wireframe',
    label: 'Wireframe web',
    description: 'Header + sidebar + main + footer.',
    build: () =>
      chainIndex([
        base({ type: 'rect', x: 80, y: 80, width: 800, height: 60, roundness: null, strokeColor: '#111827' } as Partial<CanvasElement>),
        base({ type: 'text', x: 100, y: 100, width: 100, height: 24, text: 'Logo', fontSize: 18, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Logo', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'rect', x: 80, y: 160, width: 200, height: 460, roundness: null } as Partial<CanvasElement>),
        base({ type: 'text', x: 100, y: 180, width: 100, height: 24, text: 'Sidebar', fontSize: 16, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Sidebar', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'rect', x: 300, y: 160, width: 580, height: 400, roundness: null } as Partial<CanvasElement>),
        base({ type: 'text', x: 320, y: 180, width: 100, height: 24, text: 'Main content', fontSize: 16, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Main content', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'rect', x: 300, y: 580, width: 580, height: 40, roundness: null } as Partial<CanvasElement>),
        base({ type: 'text', x: 320, y: 590, width: 100, height: 24, text: 'Footer', fontSize: 14, fontFamily: 2, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Footer', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
      ]),
  },
  {
    id: 'kanban',
    label: 'Kanban',
    description: 'Todo / Doing / Done + 3 sticky.',
    build: () =>
      chainIndex([
        base({ type: 'rect', x: 80, y: 80, width: 240, height: 500, roundness: null, fillColor: '#fef3c7', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'text', x: 160, y: 100, width: 120, height: 24, text: 'Todo', fontSize: 24, fontFamily: 3, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Todo', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'rect', x: 340, y: 80, width: 240, height: 500, roundness: null, fillColor: '#dbeafe', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'text', x: 420, y: 100, width: 120, height: 24, text: 'Doing', fontSize: 24, fontFamily: 3, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Doing', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'rect', x: 600, y: 80, width: 240, height: 500, roundness: null, fillColor: '#d1fae5', fillStyle: 'solid' } as Partial<CanvasElement>),
        base({ type: 'text', x: 690, y: 100, width: 120, height: 24, text: 'Done', fontSize: 24, fontFamily: 3, textAlign: 'left', verticalAlign: 'top', containerId: null, originalText: 'Done', autoResize: true, lineHeight: 1.25 } as Partial<CanvasElement>),
        base({ type: 'sticky', x: 100, y: 150, width: 200, height: 100, text: 'Task 1', colorScheme: 'yellow' } as Partial<CanvasElement>),
        base({ type: 'sticky', x: 360, y: 150, width: 200, height: 100, text: 'In progress', colorScheme: 'blue' } as Partial<CanvasElement>),
        base({ type: 'sticky', x: 620, y: 150, width: 200, height: 100, text: 'Done ✓', colorScheme: 'green' } as Partial<CanvasElement>),
      ]),
  },
];
